import { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PROJECT_STATUS_OPTIONS } from '@/lib/projectConstants';

export interface FilterState {
  status: string;
  client: string;
  owner: string;
  tag: string;
  statusGroup: 'all' | 'active' | 'completed';
  dueDate: 'all' | 'overdue' | 'this_week' | 'this_month';
  dealValueMin: number | null;
  dealValueMax: number | null;
  goNoGoVerdict: 'all' | 'go' | 'conditional_go' | 'no_go' | 'none';
  riskLevel: 'all' | 'high' | 'medium' | 'low';
  // Deep-link flags from the dashboard leading-indicators panel.
  stalled: boolean;
  lowQuality: boolean;
  qualityDeclining: boolean;
}

interface ProjectFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  clients: string[];
  owners?: { id: string; name: string }[];
  tags?: string[];
  onClearFilters: () => void;
}

export const defaultFilters: FilterState = {
  status: 'all',
  client: 'all',
  owner: 'all',
  tag: 'all',
  statusGroup: 'all',
  dueDate: 'all',
  dealValueMin: null,
  dealValueMax: null,
  goNoGoVerdict: 'all',
  riskLevel: 'all',
  stalled: false,
  lowQuality: false,
  qualityDeclining: false,
};

type QuickFilter = 'all' | 'active' | 'completed' | 'overdue';

export function ProjectFilters({
  filters,
  onFilterChange,
  clients,
  owners = [],
  tags = [],
  onClearFilters,
}: ProjectFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Count only advanced filters (not quick pills)
  const advancedFilterCount = [
    filters.status !== 'all',
    filters.client !== 'all',
    filters.owner !== 'all',
    filters.tag !== 'all',
    filters.dealValueMin !== null,
    filters.dealValueMax !== null,
  ].filter(Boolean).length;

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFilterChange({ ...filters, [key]: value });
  };

  // Determine current quick filter state
  const getActiveQuickFilter = (): QuickFilter => {
    if (filters.dueDate === 'overdue') return 'overdue';
    if (filters.statusGroup === 'active') return 'active';
    if (filters.statusGroup === 'completed') return 'completed';
    return 'all';
  };

  const handleQuickFilter = (filter: QuickFilter) => {
    if (filter === 'overdue') {
      onFilterChange({ ...filters, statusGroup: 'all', dueDate: 'overdue' });
    } else if (filter === 'active') {
      onFilterChange({ ...filters, statusGroup: 'active', dueDate: 'all' });
    } else if (filter === 'completed') {
      onFilterChange({ ...filters, statusGroup: 'completed', dueDate: 'all' });
    } else {
      onFilterChange({ ...filters, statusGroup: 'all', dueDate: 'all' });
    }
  };

  const activeQuickFilter = getActiveQuickFilter();

  const quickFilters: { value: QuickFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' },
  ];

  return (
    <div className="flex items-center gap-3">
      {/* Quick Filter Tabs */}
      <div className="flex items-center border-b border-border/30">
        {quickFilters.map((filter) => (
          <button
            key={filter.value}
            onClick={() => handleQuickFilter(filter.value)}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeQuickFilter === filter.value
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground/70 hover:text-foreground'
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Advanced Filters Popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all h-7',
              'text-muted-foreground hover:text-foreground',
              advancedFilterCount > 0 && 'text-primary',
              isOpen && 'text-primary'
            )}
          >
            <Filter className="w-3.5 h-3.5" />
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="h-4 px-1.5 text-[10px] rounded-full bg-primary/20 text-primary">
                {advancedFilterCount}
              </Badge>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Advanced Filters</h4>
              {advancedFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear all
                </Button>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                Status
              </label>
              <Select
                value={filters.status}
                onValueChange={(v) => updateFilter('status', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {PROJECT_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Client */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                Client
              </label>
              <Select
                value={filters.client}
                onValueChange={(v) => updateFilter('client', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client} value={client}>
                      {client}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Owner */}
            {owners.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                  Owner
                </label>
                <Select
                  value={filters.owner}
                  onValueChange={(v) => updateFilter('owner', v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {owners.map((owner) => (
                      <SelectItem key={owner.id} value={owner.id}>
                        {owner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                  Tag
                </label>
                <Select
                  value={filters.tag}
                  onValueChange={(v) => updateFilter('tag', v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {tags.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Due Date */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                Due Date
              </label>
              <Select
                value={filters.dueDate}
                onValueChange={(v) => updateFilter('dueDate', v as FilterState['dueDate'])}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="this_week">Due This Week</SelectItem>
                  <SelectItem value="this_month">Due This Month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Deal Value Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                  Min Value
                </label>
                <Input
                  type="number"
                  placeholder="$0"
                  className="h-9"
                  value={filters.dealValueMin ?? ''}
                  onChange={(e) =>
                    updateFilter(
                      'dealValueMin',
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">
                  Max Value
                </label>
                <Input
                  type="number"
                  placeholder="No limit"
                  className="h-9"
                  value={filters.dealValueMax ?? ''}
                  onChange={(e) =>
                    updateFilter(
                      'dealValueMax',
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
